"use client"

import { getProductReviews } from "../../../../lib/data/products"
import { Star, StarSolid } from "@medusajs/icons"
import { StoreProductReview } from "../../../../types/global"
import { Button } from "@medusajs/ui"
import { useState, useEffect } from "react"
import ProductReviewsForm from "./form"

type ProductReviewsProps = {
  productId: string
}

export default function ProductReviews({
  productId,
}: ProductReviewsProps) {
  const [page, setPage] = useState(1)
  const defaultLimit = 10
  const [reviews, setReviews] = useState<StoreProductReview[]>([])
  const [rating, setRating] = useState(0)
  const [hasMoreReviews, setHasMoreReviews] = useState(false)
  const [count, setCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    console.log("ProductReviews: Fetching reviews for product:", productId)
    setIsLoading(true)
    setError(null)
    
    getProductReviews({
      productId,
      limit: defaultLimit,
      offset: (page - 1) * defaultLimit,
    }).then(({ reviews: paginatedReviews, average_rating, count, limit }) => {
      console.log("ProductReviews: API response:", { 
        reviews: paginatedReviews, 
        average_rating, 
        count, 
        limit,
        reviewsLength: paginatedReviews?.length 
      })
      
      // Filter out null or invalid reviews
      const validReviews = paginatedReviews?.filter(review => 
        review && review.id && typeof review.id === 'string'
      ) || []
      
      console.log("ProductReviews: Valid reviews:", validReviews.length)
      
      setReviews((prev) => {
        const newReviews = validReviews.filter(
          (review) => !prev.some((r) => r.id === review.id)
        )
        console.log("ProductReviews: New reviews to add:", newReviews.length)
        return [...prev, ...newReviews]
      })
      setRating(Math.round(average_rating))
      console.log("ProductReviews: Updated state:", { 
        count, 
        limit, 
        page, 
        hasMore: count > limit * page,
        rating: Math.round(average_rating)
      })
      setHasMoreReviews(count > limit * page)
      setCount(count)
      setIsLoading(false)
    }).catch((err) => {
      console.error("ProductReviews: Error fetching reviews:", err)
      setError(err.message || "Napaka pri nalaganju ocen")
      setIsLoading(false)
    })
  }, [page, productId])

  function Review({ review }: { review: StoreProductReview }) {
    // Add null check for review
    if (!review || !review.id) {
      console.warn("ProductReviews: Skipping invalid review:", review)
      return null
    }

    return (
      <div className="flex flex-col gap-y-2 text-base-regular text-ui-fg-base">
        <div className="flex gap-x-2 items-center">
          {review.title && <strong>{review.title}</strong>}
          <div className="flex gap-x-1">
            {Array.from({ length: 5 }).map((_, index) => (
              <span key={index}>
                {index < review.rating ? (
                  <StarSolid className="text-ui-tag-orange-icon" />
                ) : (
                  <Star />
                )}
              </span>
            ))}
          </div>
        </div>
        <div>{review.content}</div>
        <div className="border-t border-ui-border-base pt-4 text-sm-regular">
          {review.first_name} {review.last_name}
        </div>
      </div>
    )
  }

  console.log("ProductReviews: Rendering with state:", { 
    reviews: reviews.length, 
    count, 
    rating, 
    isLoading, 
    error 
  })

  return (
    <div className="product-page-constraint">
      <div className="flex flex-col items-center text-center mb-16">
        <span className="text-base-regular text-gray-600 mb-6">
          Ocene izdelkov
        </span>
        <p className="text-2xl-regular text-ui-fg-base max-w-lg">
          {count === 0 ? "Bodite prvi, ki ocenite" : "Poglejte, kaj naše stranke o tem izdelku pravijo."}
        </p>
        <div className="flex gap-x-2 justify-center items-center">
          <div className="flex gap-x-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <span key={index}>
                {!rating || index >= rating ? (
                  <Star />
                ) : (
                  <StarSolid className="text-ui-tag-orange-icon" />
                )}
              </span>
            ))}
          </div>
          <span className="text-base-regular text-gray-600">
            {count} ocen
          </span>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <p>Nalaganje ocen...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-red-600">
          <p>Napaka: {error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 small:grid-cols-2 gap-x-6 gap-y-8">
            {reviews.map((review) => (
              <Review key={review.id} review={review} />
            ))}
          </div>

          {reviews.length === 0 && count > 0 && (
            <div className="text-center py-8">
              <p>Trenutno ni ocen za ta izdelek.</p>
            </div>
          )}

          {hasMoreReviews && (
            <div className="flex justify-center mt-8">
              <Button variant="secondary" onClick={() => setPage(page + 1)}>
                Naloži več ocen
              </Button>
            </div>
          )}
        </>
      )}

      <ProductReviewsForm productId={productId} />
    </div>
  )
} 